import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CouponTypeEnum } from '@/api/affiliate/types/Coupon'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { usePackageFeesFromRequests } from '@/api/package-req/hooks/usePackageFeesFromRequests'
import { useGetPaymentLink } from '@/api/payments/mutations/useGetPaymentLink'
import { ID_REQUIRED_THRESHOLD, TAX_FREE_THRESHOLD } from '@/api/payments/paymentsService'
import { useGetPackageTaxes } from '@/api/payments/queries/useGetPackageTaxes'
import { CheckCouponResponseData } from '@/api/payments/types/CheckCouponResponse'
import { CreatePaymentLinkPayload, PaymentMethod } from '@/api/payments/types/CreatePaymentLinkPayload'
import { PkgShippingMethod, UserPkg } from '@/api/user-package/types/UserPkg'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import AttentionIcon from '@/components/icons/attention.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { isValidIsraeliId } from '@/helpers/israeliId'
import { ShippingPrices } from '@/helpers/priceChart'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { CouponInput } from './components/CouponInput'
import { PaymentData } from './components/PaymentData'
import { PaymentOptionList } from './components/PaymentOptionList'
import { PaymentSummaryDetails } from './components/PaymentSummaryDetails'
import { BIT_MIN_ILS } from './constants'
import { ShippingOptions } from './ShippingPage'
export const calcPercentageCouponDiscountUSD = ({
  priceInUSD,
  percentageValue,
  maxDiscount,
}: {
  priceInUSD: number
  percentageValue: number
  maxDiscount?: number | null
}) => {
  const percentageDiscount = priceInUSD * (percentageValue / 100)
  const normalizedMaxDiscount = maxDiscount ?? 0
  if (normalizedMaxDiscount > 0) {
    return Math.min(percentageDiscount, normalizedMaxDiscount)
  }
  return percentageDiscount
}
interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  paymentData: PaymentData
  onUpdatePaymentData: (data: Partial<PaymentData>) => void
  shippingPrices: ShippingPrices
  insurancePrice: number
  pkgValue: number
}

export default function SummaryPage({
  pkg,
  shippingOptions,
  paymentData,
  onUpdatePaymentData,
  shippingPrices,
  insurancePrice,
  pkgValue,
}: Props) {
  const { t } = useTranslation()
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { data: currencyRates } = useCurrencyRates()
  const poalimRate = currencyRates?.poalimRate ?? 0
  const BOIrate = currencyRates?.BOIrate ?? 0
  const { data: loggedUser } = useCurrentUser()
  const { isPending: isLoadingPaymentLink, mutateAsync: getPaymentLink } = useGetPaymentLink()
  const { data: packageFees, isLoading: isLoadingPackageFees } = usePackageFeesFromRequests(pkg.id)
  const shouldFetchTaxes = pkgValue > TAX_FREE_THRESHOLD
  const { data: taxesData, isLoading: isPendingTaxes } = useGetPackageTaxes(pkg?.id, shouldFetchTaxes)
  const isLoadingTaxes = shouldFetchTaxes && isPendingTaxes
  const israeliIdRef = useRef<HTMLInputElement>(null)

  const shouldCollectIsraeliId = pkgValue > ID_REQUIRED_THRESHOLD
  const shippingPrice =
    shippingOptions.selectedShippingMethod === PkgShippingMethod.Test ? 0.1 : shippingPrices[shippingOptions.selectedShippingMethod]
  const couponDiscountAmount = useMemo(() => {
    if (!paymentData.coupon) return 0

    const coupon = paymentData.coupon
    if (coupon.shippingMethod && coupon.shippingMethod !== 'all' && coupon.shippingMethod !== shippingOptions.selectedShippingMethod) {
      return 0
    }
    switch (coupon.type) {
      case CouponTypeEnum.Percentage:
        return calcPercentageCouponDiscountUSD({
          priceInUSD: shippingPrice ?? 0,
          percentageValue: coupon.value,
          maxDiscount: coupon.maxDiscount,
        })
      case CouponTypeEnum.Fixed:
        return +coupon.value
      case CouponTypeEnum.MktUniteFee:
      case CouponTypeEnum.WarehouseFees:
        return paymentData.feesDiscount
    }
  }, [paymentData.coupon, paymentData.feesDiscount, shippingOptions.selectedShippingMethod, shippingPrice])

  const totalPrice = useMemo(() => {
    if (!shippingPrice) return 0

    const res =
      (shippingPrice ?? 0) +
      (packageFees?.uniteFee ?? 0) +
      (pkg.delayChargePrice ?? 0) +
      (pkg.taxFreeFeeUSD ?? 0) +
      (packageFees?.shrinkRequestFeeUSD ?? 0) +
      (packageFees?.photoRequestFeeUSD ?? 0) +
      (shippingOptions.isInsuranceAdded ? insurancePrice : 0) +
      (taxesData?.sum ?? 0) -
      couponDiscountAmount

    return parseFloat(res.toFixed(2))
  }, [
    shippingPrice,
    packageFees?.uniteFee,
    packageFees?.shrinkRequestFeeUSD,
    packageFees?.photoRequestFeeUSD,
    pkg.delayChargePrice,
    pkg.taxFreeFeeUSD,
    shippingOptions.isInsuranceAdded,
    insurancePrice,
    taxesData?.sum,
    couponDiscountAmount,
  ])

  const handlePayment = async () => {
    if (shouldCollectIsraeliId && !isValidIsraeliId(paymentData.israeliId)) {
      setErrorMessage(t('payment.invalidId'))
      setShowErrorModal(true)
      israeliIdRef.current?.focus()
      return
    }

    try {
      const payload: CreatePaymentLinkPayload = {
        pkgId: pkg.id,
        paymentMethod: paymentData.paymentMethod,
        shippingMethod: shippingOptions.selectedShippingMethod,
        shippingAddressId: shippingOptions.selectedAddressId,
        contentVal: pkgValue.toString(),
        couponDiscount: paymentData.feesDiscount,
        couponCode: paymentData.couponCode,
        contents: pkg.contents,
        country: pkg.warehouse,
        costumerIsraeliId: paymentData.israeliId,
      }

      if (shippingOptions.isInsuranceAdded) {
        payload.insurance = insurancePrice
      }

      if ((paymentData.creditToUseInILS ?? 0) > 0) {
        payload.creditToUseInILS = Math.min(paymentData.creditToUseInILS, maxCreditToUse, totalPriceInILS)
      }

      const { hostedFieldsData, paymentMethod } = await getPaymentLink(payload)
      if (paymentMethod === 'credit') {
        navigate(`${RoutePath.PaymentProcess}/${pkg.id}?callbackPrice=0`)
        return
      }
      onUpdatePaymentData({ hostedFieldsData: hostedFieldsData ?? null, totalPrice })
      navigate(`${RoutePath.PaymentProcess}/${pkg.id}`)
    } catch (error) {
      console.log('Payment error:', error)
      setErrorMessage(t('payment.serverError'))
      setShowErrorModal(true)
    }
  }

  const handleCouponApply = (couponData: CheckCouponResponseData) => {
    onUpdatePaymentData({
      coupon: couponData.coupon,
      feesDiscount: couponData.feesDiscount,
      couponCode: couponData.coupon.code,
    })
  }

  const getShippingPriceInILS = () => {
    const priceInUSD = parseFloat(String(shippingPrice))
    const totalPriceInILS = priceInUSD * 0.9 * 1.03 * poalimRate + priceInUSD * 0.1 * BOIrate
    return totalPriceInILS
  }

  const totalPriceInILS = useMemo(() => {
    if (!totalPrice) return 0
    return parseFloat((totalPrice * 0.9 * 1.03 * poalimRate + totalPrice * 0.1 * BOIrate).toFixed(2))
  }, [totalPrice, poalimRate, BOIrate])

  const creditBalance = loggedUser?.creditBalance ?? 0
  const maxCreditToUse = Math.min(creditBalance, totalPriceInILS)
  const amountToPayInILS = parseFloat(Math.max(0, totalPriceInILS - (paymentData.creditToUseInILS ?? 0)).toFixed(2))

  const bitOptionDisabled = amountToPayInILS > 0 && amountToPayInILS < BIT_MIN_ILS

  useEffect(() => {
    if (paymentData.paymentMethod === PaymentMethod.Bit && bitOptionDisabled) {
      onUpdatePaymentData({ paymentMethod: PaymentMethod.CreditCard })
    }
  }, [paymentData.paymentMethod, bitOptionDisabled, onUpdatePaymentData])

  const isContinueDisabled = useMemo(() => {
    return isLoadingPaymentLink || isLoadingTaxes || isLoadingPackageFees || !shippingOptions.selectedShippingMethod
  }, [isLoadingPaymentLink, isLoadingTaxes, isLoadingPackageFees, shippingOptions.selectedShippingMethod])

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>{t('payment.summaryAndConfirm')}</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>{t('payment.summarySubtitle')}</p>
      </div>

      {/* Package Card */}
      <PkgPreview pkg={pkg} currentView='all' hideActions hideStatus />
      <PaymentOptionList
        selectedPaymentMethod={paymentData.paymentMethod}
        onSelectPaymentMethod={method => onUpdatePaymentData({ paymentMethod: method })}
        bitOptionDisabled={bitOptionDisabled}
      />
      <PaymentSummaryDetails
        couponDiscountAmount={couponDiscountAmount}
        delayChargePrice={pkg.delayChargePrice ?? 0}
        taxFreeFeeUSD={pkg.taxFreeFeeUSD ?? 0}
        insurancePrice={insurancePrice}
        taxesData={taxesData}
        packageFees={packageFees}
        paymentData={paymentData}
        shippingOptions={shippingOptions}
        shippingPrices={shippingPrices}
        isLoadingTaxes={isLoadingTaxes}
        totalPrice={totalPrice}
        totalPriceInILS={totalPriceInILS}
        amountToPayInILS={amountToPayInILS}
      />

      {creditBalance > 0 && totalPriceInILS > 0 && (
        <div className='border-border-default space-y-3 rounded-lg border bg-white p-4'>
          <h3 className='text-text-primary text-sm font-medium'>{t('payment.creditSectionTitle')}</h3>
          <p className='text-text-secondary text-sm'>
            {t('payment.availableBalance')} <span className='font-medium'>{creditBalance} ₪</span>
          </p>
          <div className='flex flex-col gap-2'>
            <label className='text-text-secondary text-sm' htmlFor='credit-to-use'>
              {t('payment.amountToUseLabel', { max: maxCreditToUse.toFixed(2) })}
            </label>
            <input
              id='credit-to-use'
              type='number'
              min={0}
              max={maxCreditToUse}
              step={0.01}
              value={paymentData.creditToUseInILS ?? 0}
              onChange={e => {
                const v = parseFloat(e.target.value) || 0
                onUpdatePaymentData({
                  creditToUseInILS: Math.min(Math.max(0, v), maxCreditToUse),
                })
              }}
              className='border-border-default rounded-md border bg-white px-3 py-2 text-sm'
            />
          </div>
          {(paymentData.creditToUseInILS ?? 0) > 0 && (
            <p className='text-text-secondary text-sm'>
              {t('payment.payByCard')} <span className='font-medium'>{amountToPayInILS} ₪</span>
            </p>
          )}
        </div>
      )}

      {shouldCollectIsraeliId && (
        <div className='border-border-default space-y-2 rounded-lg border bg-white p-3'>
          <h3 className='text-text-primary text-sm font-medium'>
            {t('payment.packageValueAboveId', { threshold: ID_REQUIRED_THRESHOLD })}
          </h3>
          <TextField
            ref={israeliIdRef}
            placeholder={t('payment.idPlaceholder')}
            inputMode='numeric'
            value={paymentData.israeliId}
            onChange={e => onUpdatePaymentData({ israeliId: e.target.value })}
            className='w-full'
          />
        </div>
      )}
      {/* Taxes Info */}
      {pkgValue > 0 && (
        <div className='rounded-lg bg-blue-50 p-4'>
          {pkgValue > TAX_FREE_THRESHOLD ? (
            <div className='text-sm'>{t('payment.taxTip')}</div>
          ) : (
            <div className='text-sm'>{t('payment.taxFreeMessage', { threshold: TAX_FREE_THRESHOLD })}</div>
          )}
        </div>
      )}
      {/* Coupon Input */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>{t('payment.couponCodeLabel')}</h3>
        <CouponInput
          pkg={pkg}
          uniteFee={packageFees?.uniteFee ?? 0}
          priceInILS={getShippingPriceInILS()}
          shippingMethod={shippingOptions.selectedShippingMethod}
          coupon={paymentData.coupon}
          onApplyCoupon={handleCouponApply}
          onError={msg => {
            setErrorMessage(msg)
            setShowErrorModal(true)
          }}
        />
      </div>

      {/* Payment Button */}
      <div className='flex justify-center'>
        <Button
          loading={isLoadingPaymentLink}
          onClick={handlePayment}
          disabled={isContinueDisabled}
          className='w-full max-w-[335px]'
          size='lg'
        >
          {t('payment.continueToPaymentProcess')}
        </Button>
      </div>

      {/* Error Modal */}
      <ConfirmDialog
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        confirmText={t('common.close')}
        Icon={AttentionIcon}
        title={t('payment.attention')}
        content={errorMessage}
      />
    </div>
  )
}
